import React, { useEffect, useRef, useState, useCallback } from 'react';
import ModalCloseButton from './ModalCloseButton';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, arrayUnion, arrayRemove, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { fetchBoardLists } from '../hooks/useData';
import InfoTip from './InfoTip';

// Quick scan for active 🔔-prefixed lines in a list's content. Used by
// the share-time reminder gate so we can warn before granting access to
// a list that has reminders attached. We deliberately count only ACTIVE
// reminders (no [DONE] / 🔕 / ~~🔔 — those won't fire for anyone anyway,
// so dragging the user through a warning about them adds noise).
function countActiveBellLines(content) {
  if (!content) return 0;
  let n = 0;
  for (const line of content.split('\n')) {
    if (/^🔔↻?\s?/.test(line)) n++;
  }
  return n;
}

// Walter 2026-05-09: Walter wrote this copy himself for the share-modal
// title-info tooltip (popup_help_inventory.md §2.4). Use verbatim — it's
// the single densest piece of help in the inventory and the one Walter
// wants to react to in the live UI before the rest of the inventory
// gets mechanical-shipped.
const SHARE_TITLE_TIP = `**Two things you can share, two ways to share each.**

• **What:** an entire board (every list on it), or just one list (other lists stay private). To share a single list, use the tile's ⋯ menu → "share list".

• **How (option 1) — invite by email.** Type someone's email and pick a role: \`can edit\` lets them change anything, \`can view\` is read-only. They'll get a sign-in link; once they sign in the document appears in their "shared with me" sidebar.

• **How (option 2) — share via link.** Generate a public link anyone can open. They sign in once and join your viewer list automatically. You can revoke the link any time.

People you share with see only what you shared — never your other boards or lists.`;

/**
 * ShareModal — invite-by-email + access-list management for a board
 * or a list. Same modal structure for both; the caller passes a
 * `target` describing which doc we're sharing.
 *
 * Usage:
 *   <ShareModal
 *     open={open}
 *     onClose={...}
 *     target={{
 *       kind: 'board' | 'list',
 *       ownerUid,              // owner's uid (NOT necessarily current
 *                              //  user — see viewOnly below)
 *       boardId,               // always required
 *       listId,                // only for kind='list'
 *       title,                 // for the modal header
 *       editors: [uid],        // current state
 *       viewers: [uid],
 *       shareToken,
 *       shareTokenRole,
 *     }}
 *     pushToast={...}          // optional: toast on success/error
 *     viewOnly={false}         // when true, hide invite + remove UI;
 *                              //  used by the share-badge click on
 *                              //  surfaces where a non-owner peeks
 *                              //  who else has access. Owner row is
 *                              //  rendered as "{ownerName} (owner)"
 *                              //  instead of "you (owner)".
 *   />
 *
 * For Phase 1 we render only the email-invite UI and the participant
 * list. Public-link generation lives in Phase 2 (gated below by a
 * `publicLinks` flag that's currently always false).
 */

export default function ShareModal({ open, onClose, target, pushToast, viewOnly = false }) {
  const [emailInput, setEmailInput] = useState('');
  const [role, setRole] = useState('editor');
  const [pending, setPending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // Earlier this state machine pre-confirmed "no account for X →
  // we'll email them" before firing sendShareInvite. Walter's
  // feedback: that pre-step is friction without payoff (the inviter
  // typed the email seconds ago — they don't need a separate "yes
  // I'm sure" step). The new behavior: existing-user path adds to
  // ACL directly; new-email path auto-fires sendShareInvite. Both
  // surface a toast with the email so typos are still visible.
  // pendingInvite kept removed; the only remaining inline UI is the
  // sentInvites list + the warnMsg banner for Brevo failures.
  // Local list of just-sent email invites so the inviter sees them
  // appear in the participants list as "{email} (pending)" right
  // after clicking Send. We can't read pendingInvites/* from the
  // client (admin-only), so this state is modal-session-only — it
  // disappears when the modal closes. The Cloud Function returns
  // { token, emailed } so we can also show a "couldn't email yet"
  // warning if Brevo failed (e.g. unconfigured key in dev).
  const [sentInvites, setSentInvites] = useState([]);
  // Soft warning when sendShareInvite returned ok:true but emailed:
  // false. This means the invite row exists server-side but the
  // Brevo POST failed — usually means BREVO_API_KEY isn't set or
  // the sender domain isn't verified. The inviter sees a yellow
  // banner instead of a toast so it's hard to miss.
  const [warnMsg, setWarnMsg] = useState(null);
  // Resolved display info for participant uids. Keyed by uid.
  // Populated by a batch resolveUserDisplay call when the modal
  // opens with non-empty editors/viewers. Falls back to the raw
  // uid in render when a name+email are unavailable.
  const [userDisplay, setUserDisplay] = useState({});
  // Live-subscribed copy of the target doc's editors/viewers/
  // shareToken. Earlier the modal read these from the `target` prop,
  // which was a snapshot taken at modal-open time — so the modal's
  // own invite/remove writes (which update the underlying doc) didn't
  // refresh the visible list. Walter's test #1: invited user didn't
  // appear, removed user didn't disappear, until close+reopen. Now we
  // listen to the doc directly and merge fresh ACL state into a
  // `liveTarget` that the render reads from.
  const [liveTarget, setLiveTarget] = useState(target);
  // Public-link UI state. The token + role come from `liveTarget`
  // (subscribed to the doc), so when another tab regenerates we see
  // the new URL immediately. Local state below is just for the
  // pending button states + the copy-feedback.
  const [linkBusy, setLinkBusy] = useState(null); // 'enabling' | 'disabling' | 'regenerating' | 'copy' | null
  const [linkRole, setLinkRole] = useState('editor');
  const [linkErrorMsg, setLinkErrorMsg] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);

  // Reminder-share gate (Walter 2026-05-25). Personal-reminders model
  // means reminders on a list are private to the user who set them —
  // sharing that list grants collaborators visibility (they see the
  // 🔔 lines as text) but NOT the pings (alarms fire only for the
  // creator). On top of that, collaborators can edit/delete those
  // lines, which destructively deletes the creator's reminder doc
  // via the bell-removal-scan. Both behaviours are surprising and
  // worth a hard pause before granting access. Walter's framing:
  // "best to keep reminders off shared lists".
  //
  // Shape: `{ count, proceed, cancel, kind }`. `count` is the total
  // active 🔔 lines on the target (single list, or sum across all
  // lists on a board). `proceed` re-runs the gated action; `cancel`
  // dismisses the gate. The shownRef debounces the gate so it
  // doesn't re-fire on every invite/link click within the same
  // modal session — once you've acknowledged the warning, we don't
  // pester you again until you reopen the modal.
  const [reminderGate, setReminderGate] = useState(null);
  const reminderGateShownRef = useRef(false);
  useEffect(() => {
    if (open) {
      setEmailInput('');
      setRole('editor');
      setPending(false);
      setErrorMsg(null);
      setSentInvites([]);
      setWarnMsg(null);
      setUserDisplay({});
      setLiveTarget(target);
      setLinkBusy(null);
      setLinkErrorMsg(null);
      setCopyFeedback(null);
      // Seed link role from the live doc if a token already exists,
      // otherwise default to 'editor' — matches the email-invite
      // section's default and the spec's recommended role.
      setLinkRole(target?.shareTokenRole || 'editor');
      // Reset the reminder-share gate too — a fresh modal session
      // should re-arm the warning (the user may have closed the
      // modal specifically to clean up their reminders).
      setReminderGate(null);
      reminderGateShownRef.current = false;
    }
  }, [open, target]);

  // Subscribe to the target doc while the modal is open. Updates the
  // `editors` / `viewers` / `shareToken` / `shareTokenRole` fields
  // on liveTarget whenever the doc changes — including when the
  // modal's own invite / remove buttons fire arrayUnion / arrayRemove.
  // Static fields like `kind` / `ownerUid` / `boardId` / `listId` /
  // `title` come from the prop and aren't replaced on every snap.
  useEffect(() => {
    if (!open || !target) return undefined;
    const ref = target.kind === 'list'
      ? doc(db, 'users', target.ownerUid, 'boards', target.boardId, 'lists', target.listId)
      : doc(db, 'users', target.ownerUid, 'boards', target.boardId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setLiveTarget(prev => ({
        ...(prev || target),
        editors: Array.isArray(data.editors) ? data.editors : [],
        viewers: Array.isArray(data.viewers) ? data.viewers : [],
        shareToken: data.shareToken || null,
        shareTokenRole: data.shareTokenRole || 'viewer',
      }));
    }, (err) => {
      // Non-fatal — modal falls back to the prop snapshot. Most
      // likely cause is a transient permission error during a
      // role transition.
      console.warn('[ShareModal] target onSnapshot error', err);
    });
    return unsub;
  }, [open, target?.kind, target?.ownerUid, target?.boardId, target?.listId, target]);

  // Resolve participant uids → display info (name + email) on open.
  // Re-runs if editors/viewers change while the modal is open (e.g.
  // an invite is accepted in real time). Without this, the
  // participants list shows raw uid strings, which is unreadable.
  // In viewOnly mode we also resolve the ownerUid so the "owner" row
  // can show their actual name instead of the owner-mode "you (owner)".
  useEffect(() => {
    if (!open || !target) return undefined;
    // Pull editors/viewers from liveTarget (kept fresh by the doc
    // subscription above) rather than the static prop. That way the
    // resolveUserDisplay batch re-runs whenever a user is added or
    // removed, and the participant list rerenders with current names.
    const lt = liveTarget || target;
    const editors = Array.isArray(lt.editors) ? lt.editors : [];
    const viewers = Array.isArray(lt.viewers) ? lt.viewers : [];
    const uidSet = new Set([...editors, ...viewers]);
    if (viewOnly && target.ownerUid) uidSet.add(target.ownerUid);
    const uids = Array.from(uidSet).filter(Boolean);
    if (uids.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'resolveUserDisplay');
        const res = await fn({ uids });
        if (cancelled) return;
        const map = {};
        for (const u of (res?.data?.users || [])) {
          map[u.uid] = { displayName: u.displayName || null, email: u.email || null };
        }
        setUserDisplay(prev => ({ ...prev, ...map }));
      } catch (err) {
        // Non-fatal — rows fall back to raw uid display. Most likely
        // cause is offline or a transient function error.
        console.warn('[ShareModal] resolveUserDisplay failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, liveTarget?.editors, liveTarget?.viewers, target, viewOnly, liveTarget]);

  // Fetch pending invites we've issued for this target so they
  // persist across modal close + reopen. Without this, the only
  // pending rows visible are ones sent in the current modal session
  // (sentInvites is React state, not server-backed). We seed
  // sentInvites with the server result on open so the existing
  // render path works unchanged.
  // Skipped in viewOnly mode — non-owners can't query pendingInvites
  // (the Cloud Function checks fromUid==caller, and showing pending
  // rows to a non-owner doesn't make sense anyway).
  useEffect(() => {
    if (!open || !target?.boardId || viewOnly) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = httpsCallable(functions, 'listPendingInvitesForTarget');
        const res = await list({
          boardId: target.boardId,
          listId: target.kind === 'list' ? target.listId : null,
        });
        if (cancelled) return;
        const rows = (res?.data?.invites || []).map(inv => ({
          email: inv.toEmail,
          role: inv.role,
          emailed: inv.emailed !== false,
          token: inv.token,
        }));
        // Merge: server-known invites first, then any locally-sent
        // ones not yet in server response (race window between send
        // and refetch). De-dupe by email.
        setSentInvites(prev => {
          const seen = new Set(rows.map(r => r.email));
          const localOnly = prev.filter(p => !seen.has(p.email));
          return [...rows, ...localOnly];
        });
      } catch (err) {
        // Non-fatal: pending-invite list just won't show on first
        // open, but local sentInvites still works for the current
        // session. Most likely cause is an offline state.
        console.warn('[ShareModal] listPendingInvitesForTarget failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, target?.boardId, target?.listId, target?.kind]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /**
   * Compute the Firestore doc reference for the target. Both kinds
   * resolve to a single doc; only the path differs.
   */
  const docRef = useCallback(() => {
    if (!target) return null;
    if (target.kind === 'board') {
      return doc(db, 'users', target.ownerUid, 'boards', target.boardId);
    }
    return doc(db, 'users', target.ownerUid, 'boards', target.boardId, 'lists', target.listId);
  }, [target]);

  /**
   * gateReminderWarning — if the share target has any active 🔔 lines,
   * stash the proceed action behind a confirmation modal so the user
   * sees the consequences before granting access. Returns true if the
   * caller should proceed immediately, false if the gate is pending
   * user confirmation (in which case the caller does nothing — the
   * proceed-button on the warning re-runs the action).
   *
   * Debounced via reminderGateShownRef — once per modal session.
   *
   * For kind='list': callers MUST seed target.content (the list's
   * full text) when invoking the modal — otherwise the gate races
   * the first onSnapshot fire and a fast click skips the warning.
   * BoardPage's openShareForList does this. Walter 2026-05-25.
   *
   * For kind='board': we fetch lists ourselves via fetchBoardLists,
   * so no seed required.
   */
  const gateReminderWarning = useCallback(async (proceedFn) => {
    if (reminderGateShownRef.current) return true;
    const lt = liveTarget || target;
    if (!lt) return true;
    let count = 0;
    if (lt.kind === 'list') {
      count = countActiveBellLines(lt.content || '');
    } else if (lt.kind === 'board') {
      // Board-level share: enumerate lists on the board and sum
      // active 🔔 counts across them. Single batch read; not on the
      // hot path so the latency is fine.
      try {
        const lists = await fetchBoardLists(lt.ownerUid, lt.boardId);
        for (const l of lists) count += countActiveBellLines(l.content || '');
      } catch (e) {
        console.warn('[ShareModal] reminder-gate list fetch failed:', e);
        // Fail-open: if we can't count, don't block the share.
        return true;
      }
    }
    if (count === 0) return true;
    setReminderGate({
      count,
      kind: lt.kind,
      proceed: () => {
        reminderGateShownRef.current = true;
        setReminderGate(null);
        // Defer the proceed so the modal close animation doesn't
        // race the next state update from the action itself.
        setTimeout(() => proceedFn(), 0);
      },
      cancel: () => setReminderGate(null),
    });
    return false;
  }, [liveTarget, target]);

  const onInvite = useCallback(async () => {
    setErrorMsg(null);
    setWarnMsg(null);
    const email = emailInput.trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMsg('please enter a valid email');
      return;
    }
    if (!target) return;
    setPending(true);
    try {
      const lookup = httpsCallable(functions, 'lookupUserByEmail');
      const res = await lookup({ email });
      const data = res?.data || {};
      if (data.found) {
        // Existing-user path. Earlier we did an in-place arrayUnion
        // from the client — that worked for the ACL but skipped the
        // sharedWithMe pointer for the recipient (rules forbid the
        // inviter from writing to another user's collection). Result:
        // recipient was on the ACL but couldn't discover the share
        // from /shared-with-me. New flow goes through a Cloud
        // Function that does both writes via admin SDK.
        const fn = httpsCallable(functions, 'addExistingUserToShare');
        await fn({
          kind: target.kind,
          boardId: target.boardId,
          listId: target.kind === 'list' ? target.listId : null,
          role,
          targetUid: data.uid,
        });
        pushToast?.(`invited ${data.displayName || email}`);
      } else {
        // New-email path. Per Walter's feedback, fire sendShareInvite
        // directly — no "no account for X, click confirm to send"
        // pre-confirmation step. The toast carries the email so
        // typos still surface (and we keep a yellow warnMsg banner
        // for the rare case where Brevo failed to actually deliver).
        const send = httpsCallable(functions, 'sendShareInvite');
        const sendRes = await send({
          email,
          kind: target.kind,
          boardId: target.boardId,
          listId: target.listId || null,
          role,
        });
        const emailed = sendRes?.data?.emailed !== false;
        const token = sendRes?.data?.token || null;
        // Local sentInvites list — surfaces the new pending invite
        // in the participants list immediately, before the next
        // listPendingInvitesForTarget refetch. Idempotent: re-
        // invites of the same email replace the prior entry.
        setSentInvites(prev => {
          const filtered = prev.filter(s => s.email !== email);
          return [...filtered, { email, role, emailed, token }];
        });
        if (emailed) {
          pushToast?.(`invite sent to ${email}`);
        } else {
          setWarnMsg(
            `invite created for ${email}, but the email didn't go out (server config). check Cloud Function logs.`,
          );
        }
      }
      setEmailInput('');
      setPending(false);
    } catch (err) {
      console.error('[ShareModal] invite failed', err);
      setErrorMsg(err?.message || 'invite failed — try again');
      setPending(false);
    }
  }, [emailInput, role, docRef, pushToast, target]);

  const onRemove = useCallback(async (uid) => {
    try {
      const ref = docRef();
      await updateDoc(ref, {
        editors: arrayRemove(uid),
        viewers: arrayRemove(uid),
      });
      pushToast?.('removed from share');
    } catch (err) {
      console.error('[ShareModal] remove failed', err);
      pushToast?.('remove failed — try again');
    }
  }, [docRef, pushToast]);

  /**
   * onRevokePending — cancels a pending invite by token. Calls the
   * revokeShareInvite Cloud Function (admin SDK deletes the
   * pendingInvites/{token} doc). On success the recipient's link
   * stops working — peek/accept return "this invite was revoked".
   * Optimistically remove from local sentInvites so the row
   * disappears before the server roundtrip; if the call fails we
   * re-fetch the source of truth on next open anyway.
   */
  const onRevokePending = useCallback(async (token, email) => {
    if (!token) return;
    setSentInvites(prev => prev.filter(s => s.token !== token));
    try {
      const revoke = httpsCallable(functions, 'revokeShareInvite');
      await revoke({ token });
      pushToast?.(`revoked invite to ${email}`);
    } catch (err) {
      console.error('[ShareModal] revoke failed', err);
      pushToast?.('revoke failed — try again');
      // Re-fetch on failure so the row reappears (we removed it
      // optimistically). Cheaper than tracking a separate "in flight"
      // state per row.
      try {
        const list = httpsCallable(functions, 'listPendingInvitesForTarget');
        const res = await list({
          boardId: target.boardId,
          listId: target.kind === 'list' ? target.listId : null,
        });
        const rows = (res?.data?.invites || []).map(inv => ({
          email: inv.toEmail,
          role: inv.role,
          emailed: inv.emailed !== false,
          token: inv.token,
        }));
        setSentInvites(rows);
      } catch (_) { /* ignore */ }
    }
  }, [pushToast, target]);

  // Click-level wrappers that gate the destructive-access actions
  // through the reminder-share warning. The gate is debounced via
  // reminderGateShownRef so it only fires once per modal session;
  // subsequent invite / link-enable clicks pass through directly.
  // Both wrappers are defined here (after onInvite, before
  // onEnableLink in the file) so JS closure ordering catches them
  // even though they refer forward to onEnableLink — useCallback's
  // body isn't evaluated until call-time, and the button-click
  // closure will resolve onEnableLink correctly by then.
  // eslint-disable-next-line no-use-before-define
  const handleInviteClick = useCallback(async () => {
    // eslint-disable-next-line no-use-before-define
    const proceed = await gateReminderWarning(() => onInvite());
    if (proceed) onInvite();
  }, [gateReminderWarning, onInvite]);

  // ── Public-link actions ─────────────────────────────────────
  const onEnableLink = useCallback(async () => {
    if (!target) return;
    setLinkBusy('enabling');
    setLinkErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'enableShareLink');
      await fn({
        kind: target.kind,
        boardId: target.boardId,
        listId: target.kind === 'list' ? target.listId : null,
        role: linkRole,
      });
      // No need to set local state — liveTarget will pick up the new
      // shareToken via the doc subscription.
    } catch (err) {
      console.error('[ShareModal] enable link failed', err);
      setLinkErrorMsg(err?.message || 'could not enable link');
    } finally {
      setLinkBusy(null);
    }
  }, [target, linkRole]);

  // Reminder-share gate wrapper for the enable-link button — see
  // handleInviteClick above for the pattern. Enabling a share link
  // is just as significant as inviting an editor by email: anyone
  // with the link gains access to any reminder lines in the target.
  const handleEnableLinkClick = useCallback(async () => {
    const proceed = await gateReminderWarning(() => onEnableLink());
    if (proceed) onEnableLink();
  }, [gateReminderWarning, onEnableLink]);

  const onDisableLink = useCallback(async () => {
    if (!target) return;
    setLinkBusy('disabling');
    setLinkErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'disableShareLink');
      await fn({
        kind: target.kind,
        boardId: target.boardId,
        listId: target.kind === 'list' ? target.listId : null,
      });
    } catch (err) {
      console.error('[ShareModal] disable link failed', err);
      setLinkErrorMsg(err?.message || 'could not disable link');
    } finally {
      setLinkBusy(null);
    }
  }, [target]);

  const onRegenerateLink = useCallback(async () => {
    if (!target) return;
    if (!window.confirm(
      'Regenerate the share link?\n\n'
      + 'The current link will stop working immediately. People who already accepted will keep their access — people who haven\'t set up an account yet will need this new link.',
    )) return;
    setLinkBusy('regenerating');
    setLinkErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'regenerateShareLink');
      await fn({
        kind: target.kind,
        boardId: target.boardId,
        listId: target.kind === 'list' ? target.listId : null,
        role: linkRole,
      });
    } catch (err) {
      console.error('[ShareModal] regenerate link failed', err);
      setLinkErrorMsg(err?.message || 'could not regenerate link');
    } finally {
      setLinkBusy(null);
    }
  }, [target, linkRole]);

  const onChangeLinkRole = useCallback(async (nextRole) => {
    setLinkRole(nextRole);
    // If a link already exists, update its role server-side via the
    // enable-idempotent path (it sets shareTokenRole + index entry).
    if (!target || !(liveTarget?.shareToken)) return;
    setLinkBusy('enabling');
    setLinkErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'enableShareLink');
      await fn({
        kind: target.kind,
        boardId: target.boardId,
        listId: target.kind === 'list' ? target.listId : null,
        role: nextRole,
      });
    } catch (err) {
      console.error('[ShareModal] role change failed', err);
      setLinkErrorMsg(err?.message || 'could not change link role');
    } finally {
      setLinkBusy(null);
    }
  }, [target, liveTarget?.shareToken]);

  const onCopyLink = useCallback(async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback('copied');
      setTimeout(() => setCopyFeedback(prev => prev === 'copied' ? null : prev), 1500);
    } catch (err) {
      // Older browsers / iframe restrictions. Fall back to selecting
      // the text so the user can hit Ctrl+C themselves.
      console.warn('[ShareModal] clipboard write failed', err);
      setCopyFeedback('manual');
      setTimeout(() => setCopyFeedback(prev => prev === 'manual' ? null : prev), 2500);
    }
  }, []);

  if (!open || !target) return null;

  // Read editors/viewers from liveTarget (subscribed) so the
  // participants list updates immediately on the modal's own
  // invite/remove writes. Falls back to the prop snapshot during
  // the brief window before the snapshot listener has fired.
  const renderedTarget = liveTarget || target;
  const editors = Array.isArray(renderedTarget.editors) ? renderedTarget.editors : [];
  const viewers = Array.isArray(renderedTarget.viewers) ? renderedTarget.viewers : [];
  const allParticipants = [
    ...editors.map(uid => ({ uid, role: 'editor' })),
    ...viewers.map(uid => ({ uid, role: 'viewer' })),
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 2200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.15s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          minWidth: '380px',
          maxWidth: '460px',
          // Walter 2026-05-11: cap modal height to viewport with a
          // small margin so the close button stays reachable when the
          // participants list runs long. With 17+ invitees Walter saw
          // the modal extend below the viewport with no way to close
          // it. flex-column + sticky header keeps the X accessible at
          // the top and lets the body scroll inside.
          maxHeight: 'calc(100vh - 40px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-modal-flat)',
          fontFamily: 'var(--mono)',
          // Hide while the reminder-share gate overlay is up — the
          // warning card is smaller than this modal, and without
          // hiding the modal its (opaque) content card stays visible
          // behind the warning, bleeding text through and making the
          // two layers visually inseparable. With opacity 0 +
          // pointer-events none, the warning's own backdrop + card
          // are the only visible thing during the gate. Snaps back
          // on dismiss without a flash because the transition is
          // synchronous (no animation on the modal card itself).
          visibility: reminderGate ? 'hidden' : 'visible',
          pointerEvents: reminderGate ? 'none' : 'auto',
        }}
      >
        {/* Header — pinned at the top so the close button never
            scrolls out of reach. Sits above the scrollable body
            below; matching background so the boundary is invisible
            unless the body actually overflows. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 22px 0',
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            fontWeight: 600, color: 'var(--text)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span>
              {viewOnly ? 'shared with' : `share ${target.kind === 'list' ? 'list' : 'board'}`}
            </span>
            {target.title && (
              <span style={{
                fontWeight: 400,
                color: 'var(--text-dim)',
                fontSize: 'calc(12px * var(--ui-scale, 1))',
              }}>"{target.title}"</span>
            )}
            {/* Walter 2026-05-09: title-row info tip explaining the
                what/how of sharing. Owner-only — non-owners (viewOnly
                mode) see a participant peek and don't need the
                "two-things-two-ways" primer. */}
            {!viewOnly && (
              <InfoTip label="how sharing works">
                {SHARE_TITLE_TIP}
              </InfoTip>
            )}
          </div>
          <ModalCloseButton onClick={onClose} style={{ position: 'static' }} />
        </div>

        {/* Scrollable body — everything below the header lives here so
            a long participants list doesn't push the close button out
            of reach. minHeight:0 lets the inner flex child shrink to
            fit the parent's max-height instead of forcing its own
            content height. */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '14px 22px 20px',
        }}>
        {/* Invite-by-email row — owner-only. In viewOnly mode the
            modal becomes a read-only participant peek (no invite,
            no remove buttons). One-click flow: type email + click
            invite. Existing-user path adds to ACL directly. New-
            email path auto-fires sendShareInvite (per Walter's
            feedback — no in-modal "are you sure" pre-confirmation;
            the toast carries the email so typos still surface). */}
        {!viewOnly && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <input
              type="email"
              placeholder="invite by email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !pending) handleInviteClick(); }}
              disabled={pending}
              style={{
                flex: 1,
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                padding: '6px 10px',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                outline: 'none',
              }}
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={pending}
              style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                padding: '6px 8px',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                cursor: 'pointer',
              }}
            >
              <option value="editor">can edit</option>
              <option value="viewer">can view</option>
            </select>
            <button
              onClick={handleInviteClick}
              disabled={pending}
              style={{
                padding: '6px 12px',
                background: pending ? 'var(--accent-dim)' : 'var(--accent)',
                color: 'var(--bg)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                fontWeight: 600,
                cursor: pending ? 'wait' : 'pointer',
              }}
            >
              {pending ? '…' : 'invite'}
            </button>
          </div>
        )}

        {errorMsg && (
          <div style={{
            marginBottom: '10px',
            padding: '6px 10px',
            background: 'var(--surface2)',
            border: '1px solid var(--danger, #d04a4a)',
            borderRadius: 'var(--radius)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--danger, #d04a4a)',
          }}>
            {errorMsg}
          </div>
        )}

        {warnMsg && (
          <div style={{
            marginBottom: '10px',
            padding: '6px 10px',
            background: 'var(--surface2)',
            border: '1px solid #d4a04a',
            borderRadius: 'var(--radius)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: '#d4a04a',
            lineHeight: 1.4,
          }}>
            {warnMsg}
          </div>
        )}

        {/* Participants list */}
        <div style={{
          marginTop: '14px',
          paddingTop: '12px',
          borderTop: '1px solid var(--border)',
        }}>
          <div style={{
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text-dimmer)',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            people with access
          </div>
          {/* Owner row. In owner-mode this is the current user, so we
              just say "you (owner)". In viewOnly mode the current user
              is a guest, so we resolve target.ownerUid → name + email
              via the same userDisplay map and surface that. */}
          {viewOnly ? (() => {
            const ownerInfo = userDisplay[target.ownerUid] || {};
            const ownerLabel = ownerInfo.displayName
              || ownerInfo.email
              || 'owner';
            const ownerSub = ownerInfo.displayName && ownerInfo.email
              ? `${ownerInfo.email} · owner`
              : 'owner';
            return (
              <ParticipantRow
                label={ownerLabel}
                sublabel={ownerSub}
                removable={false}
              />
            );
          })() : (
            <ParticipantRow
              label={'you (owner)'}
              sublabel="full access"
              removable={false}
            />
          )}
          {allParticipants.length === 0 && (
            <div style={{
              padding: '8px 4px',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dimmer)',
              fontStyle: 'italic',
            }}>
              no one else has access yet
            </div>
          )}
          {allParticipants.map(p => {
            // Prefer name → email → fall back to uid. The sublabel
            // pairs the email next to the role when we have both, so
            // the inviter can confirm who they're looking at without
            // memorizing uids. While the resolveUserDisplay batch is
            // in flight on first open, rows render with the raw uid
            // and update in place when the response arrives.
            const info = userDisplay[p.uid] || {};
            const primary = info.displayName || info.email || p.uid;
            const roleLabel = p.role === 'editor' ? 'can edit' : 'can view';
            const sub = info.displayName && info.email
              ? `${info.email} · ${roleLabel}`
              : roleLabel;
            return (
              <ParticipantRow
                key={p.uid}
                label={primary}
                sublabel={sub}
                removable={!viewOnly}
                onRemove={() => onRemove(p.uid)}
              />
            );
          })}
          {/* Just-sent email invites — modal-session-local. Once the
              recipient signs up and accepts, they'll appear in the
              real participants list above. We can't read pendingInvites
              from the client (admin-only), so the only way to show the
              "we just sent X an invite" state is in-modal local state.
              Hidden entirely in viewOnly mode (non-owners shouldn't see
              who else has been invited but hasn't accepted). */}
          {!viewOnly && sentInvites.map(s => (
            <ParticipantRow
              key={`pending:${s.token || s.email}`}
              label={s.email}
              sublabel={
                s.emailed
                  ? `${s.role === 'editor' ? 'can edit' : 'can view'} · pending invite`
                  : `${s.role === 'editor' ? 'can edit' : 'can view'} · invite created (email failed)`
              }
              dim
              removable={!!s.token}
              onRemove={() => onRevokePending(s.token, s.email)}
            />
          ))}
        </div>

        {/* Public-link section — owner-only. Anyone with the link
            must sign in (no anonymous join) and is added to the
            editors/viewers ACL on first use. Token is rotatable via
            "regenerate" — invalidates the existing link while keeping
            already-accepted users' access intact. */}
        {!viewOnly && (
          <div style={{
            marginTop: '14px',
            paddingTop: '12px',
            borderTop: '1px solid var(--border)',
          }}>
            <div style={{
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dimmer)',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span>share via link</span>
              {liveTarget?.shareToken && (
                <span style={{
                  fontSize: '9px',
                  color: 'var(--accent)',
                  border: '1px solid var(--accent-dim)',
                  borderRadius: 'var(--radius)',
                  padding: '1px 5px',
                  letterSpacing: '0.4px',
                }}>
                  ACTIVE
                </span>
              )}
            </div>

            {liveTarget?.shareToken ? (
              <>
                {/* URL + copy button */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <input
                    readOnly
                    value={`https://listicity.app/share/${liveTarget.shareToken}`}
                    onClick={(e) => e.target.select()}
                    style={{
                      flex: 1,
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                      padding: '6px 10px',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(11px * var(--ui-scale, 1))',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => onCopyLink(`https://listicity.app/share/${liveTarget.shareToken}`)}
                    disabled={!!linkBusy}
                    style={{
                      padding: '6px 12px',
                      background: copyFeedback === 'copied' ? 'var(--accent-dim)' : 'var(--accent)',
                      color: 'var(--bg)',
                      border: '1px solid var(--accent)',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(11px * var(--ui-scale, 1))',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {copyFeedback === 'copied' ? '✓ copied' : copyFeedback === 'manual' ? 'select & copy' : 'copy'}
                  </button>
                </div>

                {/* Role picker + regenerate + disable */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 'calc(10px * var(--ui-scale, 1))',
                    color: 'var(--text-dim)',
                  }}>
                    new joiners:
                  </span>
                  <select
                    value={liveTarget?.shareTokenRole || linkRole}
                    onChange={(e) => onChangeLinkRole(e.target.value)}
                    disabled={!!linkBusy}
                    style={{
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                      padding: '4px 8px',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(11px * var(--ui-scale, 1))',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="editor">can edit</option>
                    <option value="viewer">can view</option>
                  </select>
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={onRegenerateLink}
                    disabled={!!linkBusy}
                    title="invalidate this link and generate a new one"
                    style={{
                      background: 'transparent',
                      color: 'var(--text-dim)',
                      border: '1px solid var(--border-bright)',
                      borderRadius: 'var(--radius)',
                      padding: '4px 10px',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(10px * var(--ui-scale, 1))',
                      cursor: linkBusy ? 'wait' : 'pointer',
                    }}
                  >
                    {linkBusy === 'regenerating' ? 'regenerating…' : 'regenerate'}
                  </button>
                  <button
                    onClick={onDisableLink}
                    disabled={!!linkBusy}
                    title="turn off the link entirely"
                    style={{
                      background: 'transparent',
                      color: 'var(--danger, #d04a4a)',
                      border: '1px solid var(--border-bright)',
                      borderRadius: 'var(--radius)',
                      padding: '4px 10px',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(10px * var(--ui-scale, 1))',
                      cursor: linkBusy ? 'wait' : 'pointer',
                    }}
                  >
                    {linkBusy === 'disabling' ? 'disabling…' : 'disable'}
                  </button>
                </div>
                <p style={{
                  margin: '8px 0 0',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  color: 'var(--text-dimmer)',
                  lineHeight: 1.5,
                }}>
                  anyone with the link must sign in. they're added to your
                  {liveTarget?.shareTokenRole === 'viewer' ? ' viewers' : ' editors'} list on first use.
                </p>
              </>
            ) : (
              <>
                {/* No active link — show enable button + role pre-pick */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    value={linkRole}
                    onChange={(e) => setLinkRole(e.target.value)}
                    disabled={!!linkBusy}
                    style={{
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                      padding: '6px 8px',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(11px * var(--ui-scale, 1))',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="editor">can edit</option>
                    <option value="viewer">can view</option>
                  </select>
                  <button
                    onClick={handleEnableLinkClick}
                    disabled={!!linkBusy}
                    style={{
                      padding: '6px 12px',
                      background: linkBusy === 'enabling' ? 'var(--accent-dim)' : 'var(--accent)',
                      color: 'var(--bg)',
                      border: '1px solid var(--accent)',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--mono)',
                      fontSize: 'calc(11px * var(--ui-scale, 1))',
                      fontWeight: 600,
                      cursor: linkBusy ? 'wait' : 'pointer',
                    }}
                  >
                    {linkBusy === 'enabling' ? 'creating…' : 'create share link'}
                  </button>
                </div>
                <p style={{
                  margin: '8px 0 0',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  color: 'var(--text-dimmer)',
                  lineHeight: 1.5,
                }}>
                  generates a link anyone can open. they'll need to sign in, then
                  they're added to your {linkRole === 'viewer' ? 'viewers' : 'editors'} list automatically.
                </p>
              </>
            )}

            {linkErrorMsg && (
              <div style={{
                marginTop: 8,
                padding: '6px 10px',
                background: 'var(--surface2)',
                border: '1px solid var(--danger, #d04a4a)',
                borderRadius: 'var(--radius)',
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                color: 'var(--danger, #d04a4a)',
              }}>
                {linkErrorMsg}
              </div>
            )}
          </div>
        )}
        </div>{/* end scrollable body */}
      </div>
      {/* Reminder-share gate overlay. Sits inside the ShareModal's
          outer overlay so a click on the dimmed-background dismisses
          both modals (matches the parent modal's onClick={onClose}
          behavior). Walter 2026-05-25: copy intentionally
          discouraging — sharing reminders is almost never what users
          want under the personal-reminders model. */}
      {reminderGate && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            reminderGate.cancel();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 2300, // above the ShareModal (2200)
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'lists-fadein 0.15s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reminder-gate-title"
            style={{
              background: 'var(--surface1)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg, 8px)',
              maxWidth: '460px',
              width: 'calc(100vw - 32px)',
              padding: '20px 22px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              color: 'var(--text)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            }}
          >
            <div
              id="reminder-gate-title"
              style={{
                fontSize: 'calc(13px * var(--ui-scale, 1))',
                fontWeight: 600,
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span aria-hidden="true">🔔</span>
              <span>
                {reminderGate.kind === 'list' ? 'This list has ' : 'This board has '}
                {reminderGate.count} reminder{reminderGate.count === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ lineHeight: 1.55, color: 'var(--text-dim)', marginBottom: '14px' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                Reminders in listicity are personal — alarms fire only for the person who set them. After sharing:
              </p>
              <ul style={{ margin: '0 0 10px 18px', padding: 0 }}>
                <li style={{ marginBottom: '6px' }}>
                  Collaborators will see the 🔔 lines as text, but they <strong style={{ color: 'var(--text)' }}>won&rsquo;t be reminded</strong> — only you will.
                </li>
                <li>
                  If anyone edits or removes a 🔔 line, your reminder may be <strong style={{ color: 'var(--text)' }}>deleted along with it</strong>.
                </li>
              </ul>
              <p style={{ margin: 0 }}>
                For both reasons, it&rsquo;s best to keep reminders on a private list, separate from anything you share. You can cancel here, move the reminders elsewhere, and try again.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={reminderGate.cancel}
                style={{
                  padding: '7px 14px',
                  background: 'var(--surface2)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  cursor: 'pointer',
                }}
                autoFocus
              >
                cancel
              </button>
              <button
                type="button"
                onClick={reminderGate.proceed}
                style={{
                  padding: '7px 14px',
                  background: 'transparent',
                  color: 'var(--text-dim)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  cursor: 'pointer',
                }}
                title="share anyway — reminders stay attached to you and may be edited by collaborators"
              >
                share anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ParticipantRow({ label, sublabel, removable, onRemove, dim }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: '6px 4px',
      gap: '10px',
      opacity: dim ? 0.7 : 1,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontStyle: dim ? 'italic' : 'normal',
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 'calc(9px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
        }}>
          {sublabel}
        </div>
      </div>
      {removable && (
        <button
          onClick={onRemove}
          style={{
            background: 'none',
            border: '1px solid var(--border-bright)',
            color: 'var(--text-dim)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            padding: '3px 8px',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = 'var(--danger, #d04a4a)';
            e.currentTarget.style.borderColor = 'var(--danger, #d04a4a)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'var(--text-dim)';
            e.currentTarget.style.borderColor = 'var(--border-bright)';
          }}
        >
          remove
        </button>
      )}
    </div>
  );
}
